import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  type ExperimentDailyActivity,
  formatRevenue,
} from "../experiments/experimentClient";
import {
  formatDay,
  recentDailyActivity,
  sortDailyNewestFirst,
} from "./monetizationModel";

const RECENT_DAYS = 14;

/** Day-by-day paygate exposure and sales, so a quiet day shows as quiet. */
export function MonetizationDailyActivityTable({
  daily,
  currency,
}: {
  daily: ExperimentDailyActivity[];
  currency: string | null;
}) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll
    ? sortDailyNewestFirst(daily)
    : recentDailyActivity(daily, RECENT_DAYS);
  const olderDays = daily.filter(
    (day) => !rows.some((row) => row.date === day.date),
  ).length;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Daily activity (UTC)</CardTitle>
        {daily.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAll((current) => !current)}
          >
            {showAll
              ? `Last ${RECENT_DAYS} days`
              : `All ${daily.length} active days`}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Day</TableHead>
              <TableHead>New contacts</TableHead>
              <TableHead>Echoes started</TableHead>
              <TableHead>Paywall hits</TableHead>
              <TableHead>Offers sent</TableHead>
              <TableHead>Checkouts</TableHead>
              <TableHead>Abandoned</TableHead>
              <TableHead>Paid orders</TableHead>
              <TableHead>Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((day) => {
              const quiet =
                !day.paywall_views &&
                !day.offers_prompted &&
                !day.paid_orders &&
                !day.assigned_contacts;
              return (
                <TableRow
                  key={day.date}
                  className={quiet ? "text-muted-foreground" : undefined}
                >
                  <TableCell className="whitespace-nowrap">
                    {formatDay(day.date)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.assigned_contacts}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.echoes_started}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.paywall_views}
                    {day.paywall_contacts ? (
                      <span className="text-xs text-muted-foreground">
                        {" "}
                        ({day.paywall_contacts} contacts)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.offers_prompted}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.checkouts_created}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.carts_abandoned || "—"}
                    {day.cart_recovery_prompts ? (
                      <span className="text-xs text-muted-foreground">
                        {" "}
                        ({day.cart_recovery_prompts} reminded)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="tabular-nums font-medium">
                    {day.paid_orders}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {day.revenue_minor
                      ? formatRevenue(day.revenue_minor, currency)
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
            {!rows.length ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground"
                >
                  No activity recorded yet.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {!showAll && olderDays > 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {olderDays} earlier active day(s) hidden.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
